import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import DataTable from '../../../components/common/datatable/DataTable'
import ButtonComponent from '../../../components/common/button/Button';

const DataDetails = (props) => {
  const [isGridReady, setIsGridReady] = useState(false);
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState('');

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const colDefData = useMemo(() => {
   return [
      {
        headerName: "User Id",
        field: "userId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "User Name",
        field: "userName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Created Date",
        field: "creationDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.creationDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.creationDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.creationDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.creationDate ?? valueA;
          const bStr = nodeB?.data?.creationDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Created Time",
        field: "lastUpdateTime",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Contract Serial#",
        field: "contractSerialNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Vendor Number",
        field: "vendorNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Vendor Name",
        field: "vendorName",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
      },
      {
        headerName: "Agreement Type",
        field: "agreementType",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Agreement Date",
        field: "agreementDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.agreementDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.agreementDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.agreementDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.agreementDate ?? valueA;
          const bStr = nodeB?.data?.agreementDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Agreement Number",
        field: "agreementNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Company Code",
        field: "companyCode",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Purchasing organization",
        field: "purchaseOrganisation",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Purchasing Group",
        field: "purchaseGroup",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Validity Start Date",
        field: "validityStartDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.validityStartDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.validityStartDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.validityStartDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.validityStartDate ?? valueA;
          const bStr = nodeB?.data?.validityStartDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Validity End Date",
        field: "validityEndDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.validityEndDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.validityEndDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.validityEndDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.validityEndDate ?? valueA;
          const bStr = nodeB?.data?.validityEndDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Terms of Payment key",
        field: "paymentTerms",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Target value for Header Area/Dist",
        field: "targetValue",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Currency Key",
        field: "currency",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        headerClass: "bef-PDT",
      },
      {
        headerName: "Planned Delivery Time",
        headerClass: "center-header",
        children: [
          {
            headerName: "Manufacturing LT",
            field: "manufacturingLeadTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: "IQD",
            field: "iqd",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClass: "small-cell",
          },
          {
            headerName: "Transit Time",
            field: "transitTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
          },
          {
            headerName: "Total PDT",
            field: "transitTime",
            sortable: true,
            resizable: false,
            unSortIcon: true,
            cellClass: "small-cell",
            valueGetter: (params) => {
              const manufacturingLeadTime =
                parseFloat(params.data.manufacturingLeadTime) || 0;
              const transitTime = parseFloat(params.data.transitTime) || 0;
              const iqd = parseFloat(params.data.iqd) || 0;

              const totalPDT = manufacturingLeadTime + transitTime + iqd;

              return totalPDT;
            },
            cellRenderer: (params) => {
              const manufacturingLeadTime =
                parseFloat(params.data.manufacturingLeadTime) || 0;
              const transitTime = parseFloat(params.data.transitTime) || 0;
              const iqd = parseFloat(params.data.iqd) || 0;

              const totalPDT = manufacturingLeadTime + transitTime + iqd;

              return totalPDT;
            },
          },
        ],
      },
      {
        headerName: "Incoterms Version",
        field: "headIncotermVersion",
        sortable: true,
        resizable: false,
        headerClass: "aft-PDT",
        unSortIcon: true,
      },
      {
        headerName: "Incoterms 1",
        field: "headIncoterms",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Incoterms Location 1",
        field: "headIncotermsLoc1",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Incoterms Location 2",
        field: "headIncotermsLoc2",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Our Reference",
        field: "ourReference",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Your Reference",
        field: "yourReference",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Different Invoicing Party",
        field: "invoicingParty",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Goods Supplier",
        field: "supplyingVendor",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Category",
        field: "category",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Item Number of Purchasing Doc",
        field: "itemPurchasingDocItemNo",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Deletion Indicator in Purchasing doc",
        field: "itemDeletionIndicator",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Item Category in Purchasing Doc",
        field: "itemPurchasingDocItemCategory",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Account Assignment",
        field: "itemAccountAssignmentCategory",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Material Number",
        field: "itemMaterialNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Short Text",
        field: "itemMaterialShortDesc",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellRenderer: (params) => renderExpandableText(params, 50)
      },
      {
        headerName: "Target Quantity*",
        field: "itemTargetQuantity",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Order Unit",
        field: "itemOrderUnit",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Net Price",
        field: "itemNetPrice",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Per",
        field: "itemPer",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Order Price Unit",
        field: "itemOrderPriceUnit",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Material Group",
        field: "itemMaterialGroup",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Plant",
        field: "itemPlant",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Storage Location",
        field: "itemStorageLocation",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Order Ack Requirement",
        field: "itemOrderAckRequirement",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Material Number Used by Supplier",
        field: "itemSupplierMaterialNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Incoterms Version",
        field: "itemIncotermVersion",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Incoterms",
        field: "itemIncoterms",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Incoterms Location 1",
        field: "itemIncotermsLoc1",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Incoterms Location 2",
        field: "itemIncotermsLoc2",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Condition Type",
        field: "conditionType",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Condition Amount",
        field: "conditionAmount",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Per",
        field: "pricingPer",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Valid From",
        field: "validFrom",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.validFrom;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.validFrom;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.validFrom ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.validFrom ?? valueA;
          const bStr = nodeB?.data?.validFrom ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Valid To",
        field: "validTo",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.validTo;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.validTo;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.validTo ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.validTo ?? valueA;
          const bStr = nodeB?.data?.validTo ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Scale Type",
        field: "scaleType",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Condition Scale Quantity",
        field: "scaleQuantity",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Condition Scale Unit of Measure",
        field: "scaleUOM",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Scale Amount",
        field: "scaleAmount",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Per",
        field: "pricingPer2",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Prior Supplier",
        field: "priorSupplier",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Confirmation Control Key",
        field: "confControl",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Tax Code",
        field: "taxCode",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Shipping Instructions",
        field: "shippingInstructions",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Pricing Cond. Currency",
        field: "pricingCondCurrency",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Open PO",
        field: "openPo",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Forecast File",
        field: "forecast",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Payment in Days",
        field: "paymentInDays",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "GR time",
        field: "goodsReceiptTime",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Control key for QM",
        field: "controlKeyQM",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Gross weight",
        field: "grossWeight",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Net weight",
        field: "netWeight",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Weight Unit",
        field: "weightUnit",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Volume",
        field: "volume",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Volume unit",
        field: "volumeUnit",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Underdelivery Tolerance",
        field: "underDeliveryTolerance",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Overdelivery Tolerance",
        field: "overDeliveryTolerance",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Unlimited Overdelivery",
        field: "unlimitedOverDeliveryAllowed",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Min. Rem. self life",
        field: "minRemainingShelfLife",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "ERS",
        field: "ers",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "GR-based invoice verif.",
        field: "grInvoiceVeri",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "GR ",
        field: "gr",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "IR",
        field: "ir",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: 'Error Details',
        field: "errorDetails",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "status-cell",
        cellRenderer: (params) => renderExpandableText(params, 50)
      },
      {
        headerName: "Contract Type",
        field: "cmType",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Status",
        field: "status",
        pinned: "right",
        sortable: true,
        resizable: false,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        unSortIcon: true,
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rowData])

  const defaultColDef = {
    sortable: true,
    filter: 'agMultiColumnFilter',
    cellStyle: {
      display: "flex",
      alignItems: "center",
    },
  }

  const gridApiRef = useRef(null);

  const onColumnChange = e => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance
    setIsGridReady(true);
  };

  useEffect(() => {
    if (isGridReady && gridApiRef?.current) {
      const savedFilterModel = localStorage.getItem("maintenanceReportFilterModel");
      if (savedFilterModel) {
        gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
        setDisableFilterBtn(true);
      }
    }
  }, [isGridReady]);

  const selectionColumnDef = useMemo(() => ({
    pinned: "left",
  }), []);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("maintenanceReportFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = () => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      localStorage.setItem("maintenanceReportFilterModel", JSON.stringify(filterModel));
      props.setRowsData(gridApiRef.current.getDisplayedRowCount());
    }
  };

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.contractHistorySeqId}`
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
          if (!params.data) return "";

          const field = params.colDef.field;
          const value = params.data[field];

          return value === null || value === undefined || value === "" ? "" : value;
      },
    },
    onFilterChanged,
  }

  return (
   <>
    <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '0 10px 0 0' }}>
      <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
        <InputLabel>Jump to Column</InputLabel>
        <Select
          id="col-select"
          value={selectedColumn}
          label="Jump to Column"
          onChange={onColumnChange}
        >
          <MenuItem value="">Select Column</MenuItem>
          {colDefData?.flatMap(col =>
            col.children
              ? col.children.map(child => (
                  <MenuItem key={child.field} value={child.field}>
                    {child.headerName || child.field}
                  </MenuItem>
                ))
              : [
                  <MenuItem key={col.field} value={col.field}>
                    {col.headerName || col.field}
                  </MenuItem>
                ]
          )}
        </Select>
      </FormControl>
      <ButtonComponent
        maxWidth="150px"
        label="Clear All Filters"
        bgColor="black"
        color="white"
        disabled={disableFilterBtn}
        onClick={clearAllFilters}
      />
    </div>
    <DataTable
      rows={props?.rowData || []}
      columns={colDefData}
      enableCheckbox={false}
      defaultColDef={defaultColDef}
      onGridReady={onGridReady}
      gridOptions={gridOptions}
      getRowStyle={props.getRowStyle}
      selectionColumnDef={selectionColumnDef}
      onRowSelected={props.onRowSelected}
      rowSelection={props.rowSelection}
      onSelectionChanged={props.onSelectionChanged}
      enableCharts={true}
      cellSelection={true}
    />
    <div style={{marginBottom: '20px'}}></div>
   </>
  )
}

export default DataDetails